import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Check, X, ExternalLink, RefreshCw, 
  CreditCard, Building2, FileText, Loader2, AlertCircle,
  Link2, Unlink, Settings, ChevronRight, Users
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';
import GoCardlessGuidedSetup from '../components/GoCardlessGuidedSetup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Integration configurations
const integrations = [
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Accept card payments from your customers',
    category: 'payments',
    logo: (
      <svg viewBox="0 0 24 24" className="w-6 h-6">
        <path fill="#635BFF" d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
      </svg>
    ),
    color: '#635BFF',
    features: ['Card payments', 'Automatic invoicing', 'Customer sync']
  },
  {
    id: 'gocardless',
    name: 'GoCardless',
    description: 'Collect payments via Direct Debit',
    category: 'payments',
    logo: (
      <svg viewBox="0 0 24 24" className="w-6 h-6">
        <circle fill="#00D5BE" cx="12" cy="12" r="10"/>
        <path fill="#FFF" d="M8 12l3 3 5-6" strokeWidth="2" stroke="#FFF" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    color: '#00D5BE',
    features: ['Direct Debit', 'Recurring payments', 'Mandate management']
  },
  {
    id: 'xero',
    name: 'Xero',
    description: 'Sync invoices and customers with Xero',
    category: 'accounting',
    logo: (
      <svg viewBox="0 0 24 24" className="w-6 h-6">
        <rect fill="#13B5EA" width="24" height="24" rx="4"/>
        <text x="12" y="16" textAnchor="middle" fill="#FFF" fontSize="8" fontWeight="bold">xero</text>
      </svg>
    ),
    color: '#13B5EA',
    features: ['Invoice sync', 'Customer sync', 'Payment reconciliation']
  },
  {
    id: 'quickbooks',
    name: 'QuickBooks',
    description: 'Sync with QuickBooks Online',
    category: 'accounting',
    logo: (
      <svg viewBox="0 0 24 24" className="w-6 h-6">
        <rect fill="#2CA01C" width="24" height="24" rx="4"/>
        <text x="12" y="16" textAnchor="middle" fill="#FFF" fontSize="10" fontWeight="bold">QB</text>
      </svg>
    ),
    color: '#2CA01C',
    features: ['Invoice sync', 'Expense tracking', 'Bank feeds']
  },
  {
    id: 'freeagent',
    name: 'FreeAgent',
    description: 'UK accounting software integration',
    category: 'accounting',
    logo: (
      <svg viewBox="0 0 24 24" className="w-6 h-6">
        <rect fill="#50C878" width="24" height="24" rx="4"/>
        <text x="12" y="16" textAnchor="middle" fill="#FFF" fontSize="8" fontWeight="bold">FA</text>
      </svg>
    ),
    color: '#50C878',
    features: ['Invoice sync', 'MTD compliant', 'Bank reconciliation']
  },
  {
    id: 'sage',
    name: 'Sage',
    description: 'Connect with Sage Business Cloud',
    category: 'accounting',
    logo: (
      <svg viewBox="0 0 24 24" className="w-6 h-6">
        <rect fill="#00D639" width="24" height="24" rx="4"/>
        <text x="12" y="16" textAnchor="middle" fill="#FFF" fontSize="8" fontWeight="bold">sage</text>
      </svg>
    ),
    color: '#00D639',
    features: ['Invoice sync', 'Contact sync', 'Tax compliance']
  }
];

const IntegrationCard = ({ integration, connection, onConnect, onDisconnect, onSyncCustomers, onSyncGCCustomers, onUseApiKey, onSaveWebhookSecret, onSaveStripeKey, onPasteStripeKey, isLoading, isPendingDisconnect }) => {
  const isConnected = connection?.status === 'connected';
  const isPending = connection?.status === 'pending';
  const [showWebhookConfig, setShowWebhookConfig] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState('');
  const [savingSecret, setSavingSecret] = useState(false);
  const [webhookMsg, setWebhookMsg] = useState('');
  const [webhookUrlCopied, setWebhookUrlCopied] = useState(false);
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/webhook/gocardless`;
  // Stripe per-user key entry
  const [showStripeConfig, setShowStripeConfig] = useState(false);
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripeKeyVisible, setStripeKeyVisible] = useState(false);
  const [savingStripeKey, setSavingStripeKey] = useState(false);
  const [stripeKeyMsg, setStripeKeyMsg] = useState('');
  // Stripe webhook signing secret (per-user, mirrors GC pattern)
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
  const [savingStripeWebhook, setSavingStripeWebhook] = useState(false);
  const [stripeWebhookMsg, setStripeWebhookMsg] = useState('');
  const stripeWebhookUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/webhook/stripe`;
  const [stripeWebhookUrlCopied, setStripeWebhookUrlCopied] = useState(false);
  
  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setWebhookUrlCopied(true);
    setTimeout(() => setWebhookUrlCopied(false), 2000);
  };
  
  const handleSaveSecret = async () => {
    if (!webhookSecret.trim() || !onSaveWebhookSecret) return;
    setSavingSecret(true);
    setWebhookMsg('');
    const result = await onSaveWebhookSecret(webhookSecret.trim());
    if (result.ok) {
      setWebhookSecret('');
      setWebhookMsg('✓ Saved — webhook signatures will now be verified');
    } else {
      setWebhookMsg(result.error || 'Failed to save');
    }
    setSavingSecret(false);
  };

  const handleSaveStripeKey = async () => {
    if (!stripeSecretKey.trim() || !onSaveStripeKey) return;
    setSavingStripeKey(true);
    setStripeKeyMsg('');
    const result = await onSaveStripeKey(stripeSecretKey.trim());
    if (result.ok) {
      setStripeSecretKey('');
      setStripeKeyMsg(`✓ Saved — connected to ${result.account_name || 'your Stripe account'}`);
    } else {
      setStripeKeyMsg(result.error || 'Failed to save');
    }
    setSavingStripeKey(false);
  };
  
  return (
    <div className={`bg-white rounded-2xl shadow-sm border-2 transition-all ${
      isConnected ? 'border-emerald-200' : 'border-transparent hover:border-zinc-200'
    }`}>
      <div className="p-6">
        <div className="flex items-start gap-4">
          {/* Logo */}
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${integration.color}15` }}
          >
            {integration.logo}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-zinc-900">{integration.name}</h3>
              {isConnected && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                  <Check size={12} />
                  Connected
                </span>
              )}
            </div>
            <p className="text-sm text-zinc-500 mb-3">{integration.description}</p>
            
            {/* Features */}
            <div className="flex flex-wrap gap-2">
              {integration.features.map((feature, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-1 bg-zinc-100 text-zinc-600 text-xs rounded-lg"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col items-end gap-2">
            {isConnected ? (
              <>
                {/* Show Sync Customers button for Stripe */}
                {integration.id === 'stripe' && onSyncCustomers && (
                  <button
                    onClick={onSyncCustomers}
                    data-testid="stripe-sync-customers-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl transition-colors text-sm font-medium"
                  >
                    <Users size={16} />
                    Sync Customers
                  </button>
                )}
                {/* Show Sync Customers button for GoCardless */}
                {integration.id === 'gocardless' && onSyncGCCustomers && (
                  <button
                    onClick={onSyncGCCustomers}
                    data-testid="gocardless-sync-customers-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-xl transition-colors text-sm font-medium"
                  >
                    <Users size={16} />
                    Sync Customers
                  </button>
                )}
                <button
                  onClick={() => onDisconnect(integration.id)}
                  disabled={isLoading}
                  data-testid={`disconnect-${integration.id}-btn`}
                  className={
                    isPendingDisconnect
                      ? "flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl transition-colors text-sm font-medium animate-pulse"
                      : "flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors text-sm font-medium"
                  }
                >
                  <Unlink size={16} />
                  {isPendingDisconnect ? 'Tap again to confirm' : 'Disconnect'}
                </button>
                {connection?.connected_email && (
                  <p className="text-xs text-zinc-400">{connection.connected_email}</p>
                )}
              </>
            ) : (
              <div className="flex flex-col gap-2">
                {/* GoCardless: API-token connect ONLY (OAuth app not configured — the
                    OAuth flow 400s at GoCardless and disconnects any existing link) */}
                {integration.id === 'gocardless' && onUseApiKey ? (
                  <button
                    onClick={onUseApiKey}
                    data-testid="gocardless-connect-api-key-btn"
                    className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-colors text-sm font-medium shadow-sm"
                  >
                    <Link2 size={16} />
                    Guided setup
                  </button>
                ) : (
                  <button
                    onClick={() => onConnect(integration.id)}
                    disabled={isLoading}
                    data-testid={`connect-${integration.id}-btn`}
                    className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white rounded-xl transition-colors text-sm font-medium shadow-sm"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Link2 size={16} />
                        {integration.id === 'stripe' ? 'Connect with Stripe' : 'Connect'}
                      </>
                    )}
                  </button>
                )}
                {integration.id === 'stripe' && onPasteStripeKey && (
                  <button
                    onClick={onPasteStripeKey}
                    className="text-xs font-medium text-zinc-500 hover:text-zinc-700 underline underline-offset-2"
                    data-testid="stripe-paste-key-btn"
                  >
                    Or paste your secret key
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Connected details */}
      {isConnected && connection?.last_sync && (
        <div className="px-6 py-3 bg-zinc-50 border-t border-zinc-100 flex items-center justify-between">
          <span className="text-xs text-zinc-500">
            Last synced: {new Date(connection.last_sync).toLocaleString()}
          </span>
          <button className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
            <RefreshCw size={12} />
            Sync now
          </button>
        </div>
      )}
      
      {/* GoCardless webhook configuration */}
      {isConnected && integration.id === 'gocardless' && (
        <div className="px-6 py-3 bg-zinc-50 border-t border-zinc-100">
          <button
            onClick={() => setShowWebhookConfig(!showWebhookConfig)}
            className="w-full flex items-center justify-between text-xs font-medium text-zinc-700 hover:text-zinc-900"
            data-testid="gocardless-webhook-config-toggle"
          >
            <span className="flex items-center gap-2">
              Webhook configuration
              {connection?.has_webhook_secret && (
                <span className="inline-flex items-center gap-1 text-emerald-600 text-[10px] font-semibold uppercase">
                  <Check size={10} /> Secret saved
                </span>
              )}
              {!connection?.has_webhook_secret && (
                <span className="inline-flex items-center gap-1 text-amber-600 text-[10px] font-semibold uppercase">
                  Secret missing
                </span>
              )}
            </span>
            <span className="text-zinc-400">{showWebhookConfig ? '▾' : '▸'}</span>
          </button>
          
          {showWebhookConfig && (
            <div className="mt-3 space-y-3" data-testid="gocardless-webhook-config-panel">
              <div>
                <p className="text-[11px] font-medium text-zinc-700 mb-1">Webhook URL</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={webhookUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-xs font-mono text-zinc-600 truncate"
                    data-testid="gocardless-webhook-url-input"
                  />
                  <button
                    onClick={copyWebhookUrl}
                    className="px-3 py-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-lg text-xs font-medium text-zinc-700"
                    data-testid="gocardless-webhook-url-copy"
                  >
                    {webhookUrlCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-[11px] text-zinc-500 mt-1">
                  Add this URL in your GoCardless dashboard → Developers → Webhook endpoints.
                </p>
              </div>
              
              <div>
                <p className="text-[11px] font-medium text-zinc-700 mb-1">Webhook Secret</p>
                <p className="text-[11px] text-zinc-500 mb-2">
                  Paste the secret GoCardless generated when you created the webhook. We verify every incoming webhook against your secret so only genuine GoCardless events trigger payment updates. <strong>This is per-account</strong> — each user has their own.
                </p>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={webhookSecret}
                    onChange={(e) => setWebhookSecret(e.target.value)}
                    placeholder={connection?.has_webhook_secret ? '••••••••••••••• (stored — enter new value to replace)' : 'Paste your GoCardless webhook secret'}
                    className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-xs font-mono text-zinc-700 placeholder:text-zinc-400"
                    data-testid="gocardless-webhook-secret-input"
                  />
                  <button
                    onClick={handleSaveSecret}
                    disabled={!webhookSecret.trim() || savingSecret}
                    className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 disabled:cursor-not-allowed rounded-lg text-xs font-medium text-white"
                    data-testid="gocardless-webhook-secret-save"
                  >
                    {savingSecret ? 'Saving…' : 'Save'}
                  </button>
                </div>
                {webhookMsg && (
                  <p className={`text-[11px] mt-1.5 ${webhookMsg.startsWith('✓') ? 'text-emerald-600' : 'text-red-600'}`} data-testid="gocardless-webhook-secret-msg">
                    {webhookMsg}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Stripe per-user secret key configuration. Multi-tenant CRM — each
          owner pastes their OWN sk_live_... here so payment links and
          auto-charges hit their balance, not a shared platform account. */}
      {isConnected && integration.id === 'stripe' && (
        <div className="px-6 py-3 bg-zinc-50 border-t border-zinc-100">
          <button
            onClick={() => setShowStripeConfig(!showStripeConfig)}
            className="w-full flex items-center justify-between text-xs font-medium text-zinc-700 hover:text-zinc-900"
            data-testid="stripe-api-key-toggle"
          >
            <span className="flex items-center gap-2">
              Stripe API key
              {connection?.account_name && (
                <span className="inline-flex items-center gap-1 text-emerald-600 text-[10px] font-semibold uppercase">
                  <Check size={10} /> {connection.account_name}
                </span>
              )}
              {connection?.environment === 'test' && (
                <span className="inline-flex items-center gap-1 text-amber-600 text-[10px] font-semibold uppercase">
                  TEST MODE
                </span>
              )}
            </span>
            <span className="text-zinc-400">{showStripeConfig ? '▾' : '▸'}</span>
          </button>

          {showStripeConfig && (
            <div className="mt-3 space-y-3" data-testid="stripe-api-key-panel">
              <p className="text-[11px] text-zinc-500">
                Paste your Stripe Secret Key so payment links and auto-charges hit your own Stripe account. Get it from{' '}
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">
                  Stripe Dashboard → Developers → API keys
                </a>. Use the <strong>Secret key</strong> (starts with <code>sk_live_</code>).
              </p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={stripeKeyVisible ? 'text' : 'password'}
                    value={stripeSecretKey}
                    onChange={(e) => setStripeSecretKey(e.target.value)}
                    placeholder="sk_live_..."
                    className="w-full pl-3 pr-9 py-2 bg-white border border-zinc-200 rounded-lg text-xs font-mono text-zinc-700 placeholder:text-zinc-400"
                    data-testid="stripe-secret-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setStripeKeyVisible(!stripeKeyVisible)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700 p-0.5"
                    data-testid="stripe-secret-key-visibility"
                  >
                    {stripeKeyVisible ? '🙈' : '👁'}
                  </button>
                </div>
                <button
                  onClick={handleSaveStripeKey}
                  disabled={!stripeSecretKey.trim() || savingStripeKey}
                  className="px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-300 disabled:cursor-not-allowed rounded-lg text-xs font-medium text-white"
                  data-testid="stripe-secret-key-save"
                >
                  {savingStripeKey ? 'Validating…' : 'Save'}
                </button>
              </div>
              {stripeKeyMsg && (
                <p className={`text-[11px] mt-1.5 ${stripeKeyMsg.startsWith('✓') ? 'text-emerald-600' : 'text-red-600'}`} data-testid="stripe-secret-key-msg">
                  {stripeKeyMsg}
                </p>
              )}
              
              {/* Stripe Webhook Secret — required for the system to know
                  when customers have paid and auto-mark invoices as paid. */}
              <div className="pt-3 mt-3 border-t border-zinc-200">
                <p className="text-[11px] font-medium text-zinc-700 mb-1">
                  Stripe Webhook Signing Secret
                  {connection?.has_webhook_secret && (
                    <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 text-[10px] font-semibold uppercase">
                      <Check size={10} /> Saved
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-zinc-500 mb-2">
                  In <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">Stripe Dashboard → Developers → Webhooks</a>, add an endpoint pointing to:
                </p>
                <div className="flex gap-1 mb-2">
                  <input
                    type="text"
                    value={stripeWebhookUrl}
                    readOnly
                    className="flex-1 px-2 py-1.5 bg-white border border-zinc-200 rounded-lg text-[11px] font-mono text-zinc-600"
                    data-testid="stripe-webhook-url-input"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(stripeWebhookUrl);
                      setStripeWebhookUrlCopied(true);
                      setTimeout(() => setStripeWebhookUrlCopied(false), 1500);
                    }}
                    className="px-2 py-1.5 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-[11px] font-medium text-zinc-700"
                    data-testid="stripe-webhook-url-copy"
                  >
                    {stripeWebhookUrlCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="text-[11px] text-zinc-500 mb-2">
                  Select events: <code>checkout.session.completed</code>, <code>payment_intent.succeeded</code>. Stripe will give you a signing secret starting with <code>whsec_</code> — paste it below.
                </p>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={stripeWebhookSecret}
                    onChange={(e) => setStripeWebhookSecret(e.target.value)}
                    placeholder={connection?.has_webhook_secret ? '••••••••••••••• (stored — enter new value to replace)' : 'whsec_...'}
                    className="flex-1 px-2 py-1.5 bg-white border border-zinc-200 rounded-lg text-[11px] font-mono text-zinc-700 placeholder:text-zinc-400"
                    data-testid="stripe-webhook-secret-input"
                  />
                  <button
                    onClick={async () => {
                      if (!stripeWebhookSecret.trim()) return;
                      setSavingStripeWebhook(true);
                      setStripeWebhookMsg('');
                      try {
                        const token = localStorage.getItem('streakfree_token');
                        const r = await fetch(`${API_URL}/api/integrations/stripe/webhook-secret`, {
                          method: 'PUT',
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ webhook_secret: stripeWebhookSecret.trim() }),
                        });
                        const data = await r.json().catch(() => ({}));
                        if (r.ok) {
                          setStripeWebhookSecret('');
                          setStripeWebhookMsg('✓ Saved — Stripe events will now be verified');
                        } else {
                          setStripeWebhookMsg(data.detail || 'Failed to save');
                        }
                      } catch (e) {
                        setStripeWebhookMsg('Network error');
                      } finally {
                        setSavingStripeWebhook(false);
                      }
                    }}
                    disabled={!stripeWebhookSecret.trim() || savingStripeWebhook}
                    className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-300 disabled:cursor-not-allowed rounded-lg text-[11px] font-medium text-white"
                    data-testid="stripe-webhook-secret-save"
                  >
                    {savingStripeWebhook ? 'Saving…' : 'Save'}
                  </button>
                </div>
                {stripeWebhookMsg && (
                  <p className={`text-[11px] mt-1.5 ${stripeWebhookMsg.startsWith('✓') ? 'text-emerald-600' : 'text-red-600'}`} data-testid="stripe-webhook-secret-msg">
                    {stripeWebhookMsg}
                  </p>
                )}
              </div>
              
              <p className="text-[11px] text-zinc-500">
                <strong>Security:</strong> stored encrypted server-side, never sent to the browser. Rotate any time without redeploying.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ConnectedApps = ({ onBack, setCurrentPage }) => {
  const [connections, setConnections] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [connectingId, setConnectingId] = useState(null);
  const [pendingDisconnectId, setPendingDisconnectId] = useState(null);
  const [error, setError] = useState(null);
  const [showGCApiKeyModal, setShowGCApiKeyModal] = useState(false);
  const [showStripeKeyModal, setShowStripeKeyModal] = useState(false);
  const [stripeModalKey, setStripeModalKey] = useState('');
  const [savingStripeModalKey, setSavingStripeModalKey] = useState(false);
  const [stripeModalMsg, setStripeModalMsg] = useState('');

  const handleSyncStripeCustomers = () => {
    if (setCurrentPage) {
      setCurrentPage('stripe-sync');
    }
  };

  const handleSyncGCCustomers = () => {
    if (setCurrentPage) {
      setCurrentPage('gocardless-sync');
    }
  };
  
  const handleSaveGCWebhookSecret = async (secret) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/integrations/gocardless/webhook-secret`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ webhook_secret: secret }),
      });
      if (res.ok) {
        // Optimistic update — mark has_webhook_secret true so badge flips
        setConnections(prev => ({
          ...prev,
          gocardless: { ...(prev.gocardless || {}), has_webhook_secret: true },
        }));
        return { ok: true };
      } else {
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.detail || 'Failed to save webhook secret' };
      }
    } catch (err) {
      return { ok: false, error: 'Network error — please try again' };
    }
  };

  // Save the user's Stripe Secret Key. Validates server-side by calling
  // stripe.Account.retrieve() — if Stripe rejects the key we surface the
  // error inline instead of silently saving a broken value. Backend will
  // also auto-create the webhook endpoint in the user's Stripe Dashboard
  // and capture the signing secret automatically so they don't have to
  // copy-paste a second value.
  const handleSaveStripeKey = async (secret) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/integrations/stripe/connect-api-key`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret_key: secret }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const webhookOk = ['auto_created', 'reused_existing'].includes(data.webhook_status);
        setConnections(prev => ({
          ...prev,
          stripe: {
            ...(prev.stripe || {}),
            status: 'connected',
            account_name: data.account_name,
            environment: data.environment,
            has_webhook_secret: webhookOk,
          },
        }));
        if (data.webhook_status === 'auto_created') {
          return { ok: true, account_name: `${data.account_name} (webhook auto-configured ✓)` };
        }
        if (data.webhook_status === 'reused_existing') {
          return { ok: true, account_name: `${data.account_name} (existing webhook reused)` };
        }
        if (data.webhook_status === 'auto_create_failed') {
          return { ok: true, account_name: `${data.account_name} — webhook auto-setup failed: ${data.webhook_error || 'unknown'}. Paste it manually below.` };
        }
        return { ok: true, account_name: data.account_name };
      } else {
        return { ok: false, error: data.detail || 'Failed to save Stripe key' };
      }
    } catch (err) {
      return { ok: false, error: 'Network error — please try again' };
    }
  };

  // Handle manual GoCardless API key connection — now handled by the
  // GoCardlessGuidedSetup wizard component.

  // Fetch current connections
  useEffect(() => {
    fetchConnections();
    // Reset connecting state on mount
    setConnectingId(null);
  }, []);

  // Check for OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const integration = params.get('integration');
    const status = params.get('status');
    const code = params.get('code');
    
    if (integration && (status === 'success' || code)) {
      // Handle OAuth callback
      handleOAuthCallback(integration, code || params.get('state'));
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      // Reset connecting state if not in callback flow
      setConnectingId(null);
    }
  }, []);

  const fetchConnections = async () => {
    try {
      const token = api.getToken();
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/integrations/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const merged = { ...(data.connections || {}) };
        // Pull the GoCardless-specific status so the UI knows whether the
        // user has saved their webhook secret. The generic /status endpoint
        // doesn't include `has_webhook_secret` — only the gocardless-scoped
        // one does. Without this the badge always says "missing" even when
        // the secret IS in the DB.
        try {
          const gcRes = await fetch(`${API_URL}/api/integrations/gocardless/status`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (gcRes.ok) {
            const gcData = await gcRes.json();
            if (gcData && gcData.status === 'connected') {
              merged.gocardless = {
                ...(merged.gocardless || {}),
                has_webhook_secret: !!gcData.has_webhook_secret,
                environment: gcData.environment,
                creditor_id: gcData.creditor_id,
              };
            }
          }
        } catch (_e) {
          // non-fatal: badge just falls back to "missing"
        }
        // Same for Stripe — pulls account name, env, and webhook secret flag
        try {
          const stRes = await fetch(`${API_URL}/api/integrations/stripe/status`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (stRes.ok) {
            const stData = await stRes.json();
            if (stData && stData.status === 'connected') {
              merged.stripe = {
                ...(merged.stripe || {}),
                account_name: stData.account_name,
                environment: stData.environment,
                has_webhook_secret: !!stData.has_webhook_secret,
              };
            }
          }
        } catch (_e) {
          // non-fatal
        }
        setConnections(merged);
      }
    } catch (err) {
      console.error('Failed to fetch connections:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (integrationId) => {
    setConnectingId(integrationId);
    setError(null);
    
    try {
      const token = api.getToken();
      console.log('Connecting to:', integrationId);
      
      const response = await fetch(`${API_URL}/api/integrations/${integrationId}/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          redirect_url: `${API_URL}/api/integrations/${integrationId}/callback`
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to initiate connection');
      }
      
      const data = await response.json();
      console.log('Connect response:', data);
      
      if (data.status === 'not_configured') {
        // One-click OAuth not set up on this server — offer the paste-key path
        setError(data.message || 'One-click connect is not available yet');
        if (integrationId === 'stripe') setShowStripeKeyModal(true);
        setConnectingId(null);
        return;
      }
      
      if (data.auth_url) {
        // Redirect to OAuth provider (Stripe, GoCardless, Xero, etc.)
        window.location.href = data.auth_url;
      } else if (data.status === 'connected') {
        // Already connected
        setConnections(prev => ({
          ...prev,
          [integrationId]: { status: 'connected', ...data }
        }));
        setConnectingId(null);
        fetchConnections();
      }
    } catch (err) {
      console.error('Connection error:', err);
      setError(err.message);
      setConnectingId(null);
    }
  };

  const handleOAuthCallback = async (integrationId, code) => {
    setConnectingId(integrationId);
    
    try {
      const token = api.getToken();
      const response = await fetch(`${API_URL}/api/integrations/${integrationId}/callback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code })
      });
      
      if (response.ok) {
        const data = await response.json();
        setConnections(prev => ({
          ...prev,
          [integrationId]: { status: 'connected', ...data }
        }));
      }
    } catch (err) {
      console.error('OAuth callback error:', err);
    } finally {
      setConnectingId(null);
      fetchConnections();
    }
  };

  const handleDisconnect = async (integrationId) => {
    // window.confirm() is unreliable in iOS PWA standalone mode, so we use
    // a state-based "click twice to confirm" pattern instead.
    if (pendingDisconnectId !== integrationId) {
      setPendingDisconnectId(integrationId);
      toast.warning(`Tap Disconnect again to confirm`, {
        description: `This will disconnect ${integrationId}. You'll need to reconnect to use it again.`,
        duration: 6000,
      });
      // Auto-clear the "armed" state after 6s
      setTimeout(() => {
        setPendingDisconnectId((current) => (current === integrationId ? null : current));
      }, 6000);
      return;
    }

    setPendingDisconnectId(null);
    setConnectingId(integrationId);

    try {
      const token = api.getToken();
      const response = await fetch(`${API_URL}/api/integrations/${integrationId}/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setConnections(prev => {
          const newConnections = { ...prev };
          delete newConnections[integrationId];
          return newConnections;
        });
        toast.success(`Disconnected from ${integrationId}`);
        fetchConnections();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(`Failed to disconnect: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Disconnect error:', err);
      toast.error('Error disconnecting integration');
    } finally {
      setConnectingId(null);
    }
  };

  const paymentIntegrations = integrations.filter(i => i.category === 'payments');
  const accountingIntegrations = integrations.filter(i => i.category === 'accounting');

  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="connected-apps-page">
      {/* Header with safe area padding */}
      <div className="bg-zinc-900 text-white px-6 py-5" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)' }}>
        <div className="flex items-center gap-4">
          {onBack && (
            <button 
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <div>
            <h1 className="text-xl font-bold">Connected Apps</h1>
            <p className="text-zinc-400 text-sm">Connect your payment and accounting services</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Error Banner */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              <AlertCircle size={20} />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-auto">
                <X size={18} />
              </button>
            </div>
          )}

          {/* Payment Integrations */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CreditCard size={20} className="text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-900">Payment Services</h2>
                <p className="text-sm text-zinc-500">Accept payments from your customers</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {paymentIntegrations.map(integration => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  connection={connections[integration.id]}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                  onSyncCustomers={integration.id === 'stripe' ? handleSyncStripeCustomers : null}
                  onSyncGCCustomers={integration.id === 'gocardless' ? handleSyncGCCustomers : null}
                  onUseApiKey={integration.id === 'gocardless' ? () => setShowGCApiKeyModal(true) : null}
                  onSaveWebhookSecret={integration.id === 'gocardless' ? handleSaveGCWebhookSecret : null}
                  onSaveStripeKey={integration.id === 'stripe' ? handleSaveStripeKey : null}
                  onPasteStripeKey={integration.id === 'stripe' ? () => { setStripeModalMsg(''); setShowStripeKeyModal(true); } : null}
                  isLoading={connectingId === integration.id}
                  isPendingDisconnect={pendingDisconnectId === integration.id}
                />
              ))}
            </div>
          </section>

          {/* Accounting Integrations */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <FileText size={20} className="text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-900">Accounting Software</h2>
                <p className="text-sm text-zinc-500">Sync invoices and customers automatically</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {accountingIntegrations.map(integration => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  connection={connections[integration.id]}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                  isLoading={connectingId === integration.id}
                  isPendingDisconnect={pendingDisconnectId === integration.id}
                />
              ))}
            </div>
          </section>

          {/* Help Section */}
          <section className="bg-gradient-to-br from-zinc-100 to-zinc-50 rounded-2xl p-6 border border-zinc-200">
            <h3 className="font-semibold text-zinc-900 mb-2">How it works</h3>
            <ul className="space-y-2 text-sm text-zinc-600">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-emerald-600 flex items-center justify-center text-xs font-bold mt-0.5">1</span>
                <span>Click <strong>Connect</strong> to authorize StreakFree with your account</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-emerald-600 flex items-center justify-center text-xs font-bold mt-0.5">2</span>
                <span>Your customers will be automatically synced when added</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-emerald-600 flex items-center justify-center text-xs font-bold mt-0.5">3</span>
                <span>Invoices and payments will sync in real-time</span>
              </li>
            </ul>
          </section>
        </div>
      </div>
      
      {/* GoCardless Guided Setup wizard */}
      <GoCardlessGuidedSetup
        open={showGCApiKeyModal}
        onClose={() => setShowGCApiKeyModal(false)}
        onComplete={fetchConnections}
      />

      {/* Stripe paste-secret-key modal (fallback when one-click OAuth is unavailable) */}
      {showStripeKeyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowStripeKeyModal(false)}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="stripe-key-modal">
            <div className="p-6 border-b border-zinc-100">
              <h2 className="text-lg font-bold text-zinc-900">Connect Stripe with your secret key</h2>
              <p className="text-sm text-zinc-500 mt-1">Stripe Dashboard → Developers → API keys → copy your <strong>Secret key</strong> (sk_live_...)</p>
            </div>
            <div className="p-6 space-y-4">
              {stripeModalMsg && (
                <div className={`p-3 rounded-xl text-sm ${stripeModalMsg.startsWith('\u2713') ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`} data-testid="stripe-key-modal-msg">
                  {stripeModalMsg}
                </div>
              )}
              <input
                type="password"
                value={stripeModalKey}
                onChange={(e) => setStripeModalKey(e.target.value)}
                placeholder="sk_live_..."
                className="w-full px-4 py-3 border border-zinc-200 rounded-xl font-mono text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="stripe-key-modal-input"
              />
              <p className="text-xs text-zinc-500">We validate the key with Stripe and set up the payment webhook automatically — nothing else to paste.</p>
            </div>
            <div className="p-6 bg-zinc-50 border-t border-zinc-100 flex gap-3">
              <button
                onClick={() => { setShowStripeKeyModal(false); setStripeModalKey(''); setStripeModalMsg(''); }}
                className="flex-1 py-3 px-4 border border-zinc-200 rounded-xl text-zinc-700 font-medium hover:bg-zinc-100 transition-colors"
                data-testid="stripe-key-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setSavingStripeModalKey(true);
                  setStripeModalMsg('');
                  const result = await handleSaveStripeKey(stripeModalKey.trim());
                  if (result.ok) {
                    setStripeModalMsg(`\u2713 Connected to ${result.account_name || 'your Stripe account'}`);
                    setStripeModalKey('');
                    setTimeout(() => { setShowStripeKeyModal(false); setStripeModalMsg(''); }, 1800);
                    fetchConnections();
                  } else {
                    setStripeModalMsg(result.error || 'Failed to save key');
                  }
                  setSavingStripeModalKey(false);
                }}
                disabled={savingStripeModalKey || !stripeModalKey.trim()}
                className="flex-1 py-3 px-4 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="stripe-key-modal-save"
              >
                {savingStripeModalKey ? (<><Loader2 size={18} className="animate-spin" /> Connecting...</>) : (<><Check size={18} /> Connect</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectedApps;
